import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { useFetch } from '@reveni-io/reveni-ui'

import { dimensionUnits, weightUnits } from '@/common/constants'
import configurationServices from '@/configuration/services/services'
import routes from '@/Router/routes.constants'

import { getPackagingPayload } from './helpers'

const usePackagingDetail = ({ id, storeId, setToastInfo }) => {
  const isNew = id === 'new'
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const selectedPackaging = location?.state?.selectedPackaging

  const {
    data,
    loading: fetchingPackaging,
    setData,
    resetData,
    hasChanges,
  } = useFetch(configurationServices.getPackagingDetail, {
    doInitialRequest: !isNew,
    params: { storeId, packagingId: id },
  })

  const initializedRef = useRef(false)

  useEffect(() => {
    if (isNew && selectedPackaging && !initializedRef.current) {
      setData(selectedPackaging)
      initializedRef.current = true
    }
  }, [isNew, selectedPackaging, setData])

  const { fetchData: createPackaging, loading: creatingPackaging } = useFetch(configurationServices.createPackaging, {
    doInitialRequest: false,
  })
  const { fetchData: editPackaging, loading: editingPackaging } = useFetch(configurationServices.editPackaging, {
    doInitialRequest: false,
  })

  const onChange = (field, value) => {
    setData(prevData => ({
      ...prevData,
      [field]: value,
    }))
  }

  const isFormValid = () => {
    const requiredFields = [data?.name, data?.type, data?.length, data?.width, data?.height, data?.weight]
    return requiredFields.every(field => field)
  }

  const onSaveChanges = async () => {
    const payload = getPackagingPayload({
      name: data?.name,
      type: data?.type,
      length: data?.length,
      width: data?.width,
      height: data?.height,
      dimensionUnit: data?.dimension_unit || dimensionUnits[0].value,
      weight: data?.weight,
      weightUnit: data?.weight_unit || weightUnits[0].value,
      isDefault: data?.is_default,
    })

    try {
      if (isNew) {
        await createPackaging({ storeId, payload })
        setToastInfo({ show: true, message: t('Embalaje creado'), type: 'success' })
        navigate(routes.configurationPage.logistics.packaging)
      } else {
        await editPackaging({ storeId, packagingId: id, payload })
        setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
        resetData()
      }
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  return {
    data,
    onChange,
    hasChanges,
    isFormValid,
    onSaveChanges,
    creatingPackaging,
    editingPackaging,
    fetchingPackaging,
    resetData,
  }
}

export default usePackagingDetail
